import { Entity } from "../entity/entity"
import {
  AttackType,
  Direction,
  EnemyType,
  EntryLocation,
  ItemType,
  MapID,
  MapType,
  PaletteColor,
  PopupID,
} from "./enums"

type Attack = { amount: number; type: AttackType }
type PigmentAttackResult = { amount: number; color: string }
type Beast = {
  type: EnemyType
  name: string
  symbol: string
  color: string
  description: string
  seen: boolean
  killCount: number
}
type BindablePigment = { color: PaletteColor; bindable: boolean }
type BinderSet = {
  jan: number
  feb: number
  mar: number
  apr: number
  may: number
  jun: number
  jul: number
  aug: number
  sep: number
  oct: number
  nov: number
  dec: number
}
type BlinkPosition = { x: number; y: number; on: boolean }
type Bounds = {
  x1: number
  y1: number
  x2: number
  y2: number
}
type CollectibleSet = {
  jan?: Collectible
  feb?: Collectible
  mar?: Collectible
  apr?: Collectible
  may?: Collectible
  jun?: Collectible
  jul?: Collectible
  aug?: Collectible
  sep?: Collectible
  oct?: Collectible
  nov?: Collectible
  dec?: Collectible
}
type CombatConfig = {
  essence: number
  maxEssence: number
  essenceDefense: number
  pigmentDefense?: number
  stealthRange?: number
  minAttack: number
  maxAttack: number
  attackType: AttackType
  attackMessage: string
}
type CorridorSpec = {
  room: number
  length: number
  entry: EntryLocation
  direction: Direction
}
type DoorSpec = {
  room: number
  location: EntryLocation
  target: MapID
  unlockDate: string // ISO8601 "YYYY-MM-DD"
}
type DropItem = { type: ItemType; amount: number }
type DropItemConfig = { type: ItemType; range: MinMaxRange }
type EnemyConfig = {
  name: string
  experience: number
  combat: CombatConfig
  render: RenderConfig
  optic?: ItemType
  direction?: Direction
}
type EnemySpec = {
  type: EnemyType
  min: number
  max: number
  mapMax: number
}
type EntityStack = { position: Position; stack: Entity[] }
type Graphic = {
  symbol: string
  fg: string
  bg: string
}
type MapBinder = {
  minMap: number
  maxMap: number
  maxEver: number
}
type MapItem = {
  type: ItemType
  amount?: number // e.g., 20 yellow pigment
  minMax?: MinMaxRange
  dropNearPlayer?: boolean
  row?: number // required for non-random placement
  column?: number // required for non-random placement
  keyTarget?: MapID // required if item is a key
  textContents?: string // required for hints
}
type MapData = {
  type: MapType
  music?: MusicSpec
  width: number
  height: number
  viewportWidth: number
  viewportHeight: number
  portals: PortalSpec[]
  player: number
  enableFogOfWar: boolean

  randomOptions?: {
    roomWidthRange: [number, number]
    roomHeightRange: [number, number]
    maxFillPercentage: number
    timeLimit: number
  }
  enemies?: EnemySpec[]
  palette?: PaletteSpec
  binder?: MapBinder
  items?: MapItem[]
  equipment?: MapItem[]
  popups?: PopupSpec[]
  rooms?: RoomSpec[]
  mazeRooms?: MazeRoomOptions[]
  corridors?: CorridorSpec[]
  doors?: DoorSpec[]
}
type MazeRoomOptions = {
  prefabSelection?: number // random prefab is undefined in config
  colors: [string, number][] // for a randomized distribution of traps by color
}
type Message = {
  count: number
  message: string
}
type MinMaxRange = { min: number; max: number }
type MusicSpec = {
  source: string
  title: string
}
type Offset = { xOffset: number; yOffset: number }
type Palette = {
  red: number
  green: number
  blue: number
  yellow: number
  magenta: number
  cyan: number
}
type PaletteSpec = {
  red?: { range: MinMaxRange; mapMax: number }
  green?: { range: MinMaxRange; mapMax: number }
  blue?: { range: MinMaxRange; mapMax: number }
  yellow?: { range: MinMaxRange; mapMax: number }
  magenta?: { range: MinMaxRange; mapMax: number }
  cyan?: { range: MinMaxRange; mapMax: number }
}
type PigmentDrop = {
  type: ItemType
  amount: number
}
type EquippableItem = {
  type: ItemType
  name: string
  equipped: boolean
  twoHanded: boolean
  minEssenceAttack: number
  maxEssenceAttack: number
  essenceDefense: number
  minPigmentAttack: number
  maxPigmentAttack: number
  pigmentDefense: number
  stealthRange: number
  levelUnlock?: number
  usages?: number
}
type PlayerEquipment = {
  head: EquippableItem
  torso: EquippableItem
  feet: EquippableItem
  leftHand: EquippableItem
  rightHand: EquippableItem
}
type PlayerVisits = {
  january: number
  february: number
  march: number
  april: number
  may: number
  june: number
  july: number
  august: number
  september: number
  october: number
  november: number
  december: number
}
type PopupSpec = {
  row: number
  column: number
  target: PopupID
  type: ItemType
}
type PortalSpec = {
  room: number
  target: MapID
}
type Position = { x: number; y: number }
type RenderConfig = { symbol: string[]; light: string; dark: string }
type RenderSymbol = { symbol: string; color: string }
type RenderText = { text: string; color: RGB }
type RGB = {
  r: number
  g: number
  b: number
  a?: number
}
type RoomSpec = {
  x: number
  y: number
  width: number
  height: number
  name?: string
  entry?: EntryLocation
}
type TapestryGraphic = {
  symbol: string
  fg: RGB
  bg: RGB
}
type TapestryStats = {
  percentCompleted: number
  tilesCompleted: number
  totalColorTiles: number
}
type TapestryTile = {
  symbol: string
  fg: RGB
  bg: RGB
}
type Tile = {
  walkable: boolean
  dark: Graphic
  light: Graphic
  transparent: boolean
  visible: boolean
  seen: boolean
}
type Collectible = { type: ItemType; name: string; found: string }

export {
  Attack,
  Beast,
  BindablePigment,
  BinderSet,
  BlinkPosition,
  Bounds,
  Collectible,
  CollectibleSet,
  CombatConfig,
  CorridorSpec,
  DoorSpec,
  DropItem,
  DropItemConfig,
  EnemyConfig,
  EnemySpec,
  EntityStack,
  EquippableItem,
  Graphic,
  MapBinder,
  MapData,
  MapItem,
  MazeRoomOptions,
  Message,
  MinMaxRange,
  MusicSpec,
  Offset,
  Palette,
  PaletteSpec,
  PigmentAttackResult,
  PigmentDrop,
  PlayerEquipment,
  PlayerVisits,
  PopupSpec,
  PortalSpec,
  Position,
  RenderConfig,
  RenderSymbol,
  RenderText,
  RGB,
  TapestryGraphic,
  TapestryStats,
  TapestryTile,
  Tile,
}
